import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Zap, Clock, Shield, Code, ChevronDown } from 'lucide-react';
import { getUTMParams, trackLead, trackEvent } from './Analytics';

// =============================================================================
// CAMPAIGN CONFIGURATIONS
// =============================================================================

const CAMPAIGNS = {
  'ai-utvikling': {
    title: 'Bygg din idé med AI',
    subtitle: 'Fra konsept til produkt på uker, ikke måneder',
    description: 'Vi bruker moderne AI-agenter som utvikler software kontinuerlig. Det som tar tradisjonelle team måneder, bygger vi på dager.',
    video_match: true, // This campaign matches the video content
    benefits: [
      'Avanserte apper og web-plattformer',
      'Komplekse API-integrasjoner',
      'Fra idé til prototype på 2-4 uker',
      'Erfarne forretningsfolk som rådgivere',
    ],
    cta: 'Send melding',
    stats: [
      { value: '5x', label: 'Raskere' },
      { value: '50%', label: 'Billigere' },
      { value: '100%', label: 'Din kode' },
    ],
  },
  'app-utvikling': {
    title: 'Få appen din bygget',
    subtitle: 'Native iOS & Android på rekordtid',
    description: 'Vi bygger komplette, produksjonsklare apper med moderne teknologi. Inkluderer betaling, brukerautentisering og alt du trenger.',
    benefits: [
      'Native iOS og Android',
      'Betalingsintegrasjon (Stripe/Vipps)',
      'Push-varsler og sanntidsfunksjoner',
      'Admin-dashboard inkludert',
    ],
    cta: 'Fortell oss om appen',
    stats: [
      { value: '300k', label: 'Pilot-pris' },
      { value: '2-4', label: 'Uker' },
      { value: '24/7', label: 'Support' },
    ],
  },
  'mvp': {
    title: 'Test idéen din raskt',
    subtitle: 'Minimal Viable Product på 2-4 uker',
    description: 'Ikke bruk måneder på å bygge noe ingen vil ha. Vi hjelper deg å validere idéen din med en ekte, fungerende MVP.',
    benefits: [
      'Komplett, fungerende produkt',
      'Klar for ekte brukertesting',
      'Betalingsløsning inkludert',
      'Full kildekode overlevert',
    ],
    cta: 'Valider idéen din',
    stats: [
      { value: '300k', label: 'Fastpris' },
      { value: '100%', label: 'Eierskap' },
      { value: '0', label: 'Lock-in' },
    ],
  },
};

const DEFAULT_CAMPAIGN = CAMPAIGNS['ai-utvikling'];

// =============================================================================
// COLORS
// =============================================================================

const c = {
  bg: '#000000',
  bgAlt: 'rgba(255, 255, 255, 0.02)',
  bgCard: 'rgba(255, 255, 255, 0.03)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.7)',
  textSubtle: 'rgba(255, 255, 255, 0.5)',
  border: 'rgba(255, 255, 255, 0.08)',
  accent: '#007AFF',
};

// =============================================================================
// LEAD FORM COMPONENT
// =============================================================================

function LeadForm({ campaign, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const utmParams = getUTMParams();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          ...utmParams,
          page_url: window.location.href,
          referrer: document.referrer,
        }),
      });
      
      // Always read as text first, then parse
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse response:', text);
        throw new Error('Serverfeil. Prøv igjen senere.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Noe gikk galt');
      }
      
      // Track conversion
      trackLead({ ...formData, ...utmParams });
      trackEvent('Lead');
      
      onSuccess();
    } catch (err) {
      setError(err.message || 'Kunne ikke sende melding. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gap: '16px' }}>
        <div>
          <input
            type="text"
            placeholder="Ditt navn *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '15px',
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${c.border}`,
              borderRadius: '10px',
              color: c.text,
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
            onBlur={(e) => e.target.style.borderColor = c.border}
          />
        </div>
        
        <div>
          <input
            type="email"
            placeholder="E-post *"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '15px',
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${c.border}`,
              borderRadius: '10px',
              color: c.text,
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
            onBlur={(e) => e.target.style.borderColor = c.border}
          />
        </div>
        
        <div>
          <input
            type="tel"
            placeholder="Telefon"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '15px',
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${c.border}`,
              borderRadius: '10px',
              color: c.text,
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
            onBlur={(e) => e.target.style.borderColor = c.border}
          />
        </div>
        
        <div>
          <textarea
            placeholder="Fortell kort om idéen din..."
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={3}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '15px',
              background: 'rgba(255,255,255,0.05)',
              border: `1px solid ${c.border}`,
              borderRadius: '10px',
              color: c.text,
              outline: 'none',
              resize: 'vertical',
              minHeight: '80px',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
            onBlur={(e) => e.target.style.borderColor = c.border}
          />
        </div>
        
        {error && (
          <div style={{ color: '#ff6b6b', fontSize: '13px' }}>{error}</div>
        )}
        
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '16px 24px',
            fontSize: '15px',
            fontWeight: '500',
            background: c.text,
            color: c.bg,
            border: 'none',
            borderRadius: '10px',
            cursor: loading ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            opacity: loading ? 0.7 : 1,
            transition: 'transform 0.2s, opacity 0.2s',
          }}
          onMouseOver={(e) => !loading && (e.target.style.transform = 'scale(1.02)')}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          {loading ? 'Sender...' : campaign.cta}
          {!loading && <ArrowRight size={18} />}
        </button>
        
        <p style={{
          fontSize: '11px',
          color: c.textSubtle,
          textAlign: 'center',
          margin: 0,
        }}>
          Ved å sende inn godtar du at vi kontakter deg. Ingen spam.
        </p>
      </div>
    </form>
  );
}

// =============================================================================
// SUCCESS STATE
// =============================================================================

function SuccessState() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        textAlign: 'center',
        padding: '40px 20px',
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', delay: 0.1 }}
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '2px solid rgba(34, 197, 94, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <Check size={32} color="#22c55e" />
      </motion.div>
      
      <h3 style={{ fontSize: '22px', color: c.text, marginBottom: '8px', fontWeight: '500' }}>
        Takk for din henvendelse!
      </h3>
      <p style={{ fontSize: '15px', color: c.textMuted, marginBottom: '24px' }}>
        Vi tar kontakt innen 24 timer.
      </p>
      
      <Link
        to="/"
        style={{
          fontSize: '14px',
          color: c.textSubtle,
          textDecoration: 'none',
        }}
      >
        ← Tilbake til forsiden
      </Link>
    </motion.div>
  );
}

// =============================================================================
// MAIN CAMPAIGN PAGE
// =============================================================================

export default function CampaignPage() {
  const { slug } = useParams();
  const [submitted, setSubmitted] = useState(false);
  
  const campaign = CAMPAIGNS[slug] || DEFAULT_CAMPAIGN;
  
  // Track page view
  useEffect(() => {
    trackEvent('ViewContent', { content_name: slug });
  }, [slug]);
  
  return (
    <div style={{
      minHeight: '100vh',
      background: c.bg,
      color: c.text,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@200;300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.2); }
        html, body { background: #000 !important; }
        input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
      
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '20px 24px',
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: '24px', fontWeight: '100', color: c.text }}>a.</span>
          </Link>
          <Link
            to="/"
            style={{
              fontSize: '13px',
              color: c.textMuted,
              textDecoration: 'none',
            }}
          >
            autonomous.no
          </Link>
        </div>
      </nav>
      
      {/* Main Content */}
      <main style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '120px 24px 80px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '60px',
        alignItems: 'start',
      }}>
        {/* Left: Content */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 style={{
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '200',
              lineHeight: 1.1,
              marginBottom: '16px',
            }}>
              {campaign.title}
            </h1>
            
            <p style={{
              fontSize: 'clamp(18px, 3vw, 22px)',
              color: c.textMuted,
              marginBottom: '32px',
              fontWeight: '300',
            }}>
              {campaign.subtitle}
            </p>
            
            <p style={{
              fontSize: '16px',
              color: c.textMuted,
              lineHeight: 1.7,
              marginBottom: '40px',
            }}>
              {campaign.description}
            </p>
            
            {/* Benefits */}
            <div style={{ marginBottom: '48px' }}>
              {campaign.benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.1 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '14px',
                  }}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Check size={12} color="#22c55e" />
                  </div>
                  <span style={{ fontSize: '15px', color: c.text }}>{benefit}</span>
                </motion.div>
              ))}
            </div>
            
            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
              padding: '24px',
              background: c.bgCard,
              borderRadius: '16px',
              border: `1px solid ${c.border}`,
            }}>
              {campaign.stats.map((stat, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '200', color: c.text, marginBottom: '4px' }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '12px', color: c.textSubtle }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Right: Form */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              background: c.bgCard,
              border: `1px solid ${c.border}`,
              borderRadius: '20px',
              padding: '32px',
              position: 'sticky',
              top: '100px',
            }}
          >
            <AnimatePresence mode="wait">
              {submitted ? (
                <SuccessState key="success" />
              ) : (
                <motion.div
                  key="form"
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: c.text,
                  }}>
                    La oss snakke
                  </h2>
                  <p style={{
                    fontSize: '14px',
                    color: c.textMuted,
                    marginBottom: '24px',
                  }}>
                    Fortell oss om idéen din, så tar vi kontakt innen 24 timer.
                  </p>
                  
                  <LeadForm campaign={campaign} onSuccess={() => setSubmitted(true)} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </main>
      
      {/* Trust Bar */}
      <section style={{
        borderTop: `1px solid ${c.border}`,
        padding: '40px 24px',
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          gap: '48px',
          flexWrap: 'wrap',
        }}>
          {[
            { icon: Zap, text: 'AI-drevet utvikling' },
            { icon: Clock, text: 'Raskere enn tradisjonelt' },
            { icon: Shield, text: 'Ingen lock-in' },
            { icon: Code, text: '100% din kode' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <item.icon size={16} color={c.textSubtle} />
              <span style={{ fontSize: '13px', color: c.textSubtle }}>{item.text}</span>
            </div>
          ))}
        </div>
      </section>
      
      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${c.border}`,
        padding: '32px 24px',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: '12px', color: c.textSubtle }}>
          © {new Date().getFullYear()} Autonomous AS · Org.nr 936 856 055
        </p>
      </footer>
    </div>
  );
}
